/**
 * Yoink Adventures V2 - Auth Diagnostics
 *
 * On-page visibility into the auth/session lifecycle for devices without
 * DevTools (the primary test device is an iPad). Enable by adding ?authdiag=1
 * to the URL, or persistently with localStorage 'yoink-authdiag' = '1'
 * (the URL param sets the flag, so it survives in-app navigation).
 *
 * Modules record events via record(); when enabled, a small collapsible panel
 * shows the timeline plus a live status line. Recording is always on (it's a
 * cheap ring buffer) so the panel can show history from before it was opened
 * in the same page load.
 *
 * Dependency-free by design: supabase-gateway imports this, so it must not
 * import gateway/state/logger.
 */

const MAX_ENTRIES = 50;
const entries = [];

let panelEl = null;
let listEl = null;
let statusEl = null;
let statusProvider = null;

/**
 * Check whether the diagnostics panel should be shown
 * @returns {boolean}
 */
export function isEnabled() {
  try {
    if (new URLSearchParams(window.location.search).has('authdiag')) {
      // Persist so the panel survives in-app navigation (router rewrites URLs)
      try { localStorage.setItem('yoink-authdiag', '1'); } catch { /* private mode */ }
      return true;
    }
    return localStorage.getItem('yoink-authdiag') === '1';
  } catch {
    return false;
  }
}

/**
 * Record an auth lifecycle event
 * @param {string} event - Short event name (e.g., 'session-recovered')
 * @param {string} detail - Optional detail text
 */
export function record(event, detail = '') {
  entries.push({ at: new Date(), event, detail: String(detail) });
  if (entries.length > MAX_ENTRIES) entries.shift();
  if (panelEl) renderEntries();
}

/**
 * Register a callback that returns current auth status for the panel header
 * @param {function} fn - Returns an object of label -> value strings
 */
export function setStatusProvider(fn) {
  statusProvider = fn;
}

/**
 * Get recorded entries (for tests and debugging)
 * @returns {Array<{at: Date, event: string, detail: string}>}
 */
export function getEntries() {
  return entries.slice();
}

function escapeText(str) {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

function renderStatus() {
  if (!statusEl) return;
  let status = {};
  try {
    status = statusProvider ? statusProvider() : {};
  } catch (err) {
    status = { error: err.message };
  }
  statusEl.innerHTML = Object.entries(status)
    .map(([k, v]) => `<div><strong>${escapeText(k)}:</strong> ${escapeText(String(v))}</div>`)
    .join('');
}

function renderEntries() {
  if (!listEl) return;
  renderStatus();
  listEl.innerHTML = entries
    .slice()
    .reverse()
    .map(e => {
      const time = e.at.toTimeString().slice(0, 8);
      const detail = e.detail ? ` — ${escapeText(e.detail)}` : '';
      return `<div>${time} <strong>${escapeText(e.event)}</strong>${detail}</div>`;
    })
    .join('');
}

/**
 * Initialize the diagnostics panel (no-op unless enabled)
 * Call after the DOM is ready.
 */
export function init() {
  if (!isEnabled() || panelEl || !document.body) return;

  panelEl = document.createElement('details');
  panelEl.id = 'auth-diag-panel';
  panelEl.style.cssText = [
    'position:fixed', 'bottom:8px', 'left:8px', 'z-index:99999',
    'max-width:90vw', 'max-height:45vh', 'overflow:auto',
    'background:rgba(0,0,0,0.85)', 'color:#9f9', 'border-radius:8px',
    'font:11px/1.5 monospace', 'padding:6px 10px'
  ].join(';');

  const summary = document.createElement('summary');
  summary.textContent = '🔐 Auth diag';
  summary.style.cssText = 'cursor:pointer;color:#fff';
  panelEl.appendChild(summary);

  statusEl = document.createElement('div');
  statusEl.style.cssText = 'border-bottom:1px solid #555;padding:4px 0;color:#ff9';
  panelEl.appendChild(statusEl);

  listEl = document.createElement('div');
  listEl.style.cssText = 'padding-top:4px';
  panelEl.appendChild(listEl);

  const offBtn = document.createElement('button');
  offBtn.textContent = 'Turn off diagnostics';
  offBtn.style.cssText = 'margin-top:6px;font:11px monospace;padding:2px 8px';
  offBtn.addEventListener('click', () => {
    try { localStorage.removeItem('yoink-authdiag'); } catch { /* ignore */ }
    panelEl.remove();
    panelEl = null;
    listEl = null;
    statusEl = null;
  });
  panelEl.appendChild(offBtn);

  document.body.appendChild(panelEl);

  // Refresh the status line when the panel is opened
  panelEl.addEventListener('toggle', () => {
    if (panelEl.open) renderEntries();
  });

  record('diagnostics-enabled');
  renderEntries();
}
